import { useTranslation } from 'react-i18next';
import { rd, RemoteData } from '@passionware/monads';
import { ReactNode } from 'react';
import { useWatch } from 'react-hook-form';
import type {
  EntityRefEntity,
  EntityRefTarget,
  FieldSpecOf
} from 'v5/core/entity-schema/entitySchema.types';
import { FormField } from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { overlayVariants } from 'v5/design-system/InputOverlay';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import { Skeleton } from 'v5/design-system/Skeleton';
import { renderError } from 'v5/patterns/widgets/renderError';
import { useFieldContext } from '../FieldContext';
import { AsyncSelector, AsyncSelectorSource } from './AsyncSelector';
import {
  type EntityCardData,
  EntityHoverCard
} from 'v5/patterns/widgets/EntityHoverCard';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponent } from './fieldComponent.types';

/** The target of an `entity-ref` field whose entity is `E`. */
type TargetOf<E extends EntityRefEntity> = Extract<
  EntityRefTarget,
  { entity: E }
>;

/** How the form finds, shows and opens one entity; the host brings one per entity. */
export type EntityRefSource<E extends EntityRefEntity> = {
  /** What the entity is called in the field's prompts ("resource": “Select a resource”). */
  noun: string;
  /** The entity's name when the form already holds it, without a request; a search over the form's values reads it. */
  nameOf?(id: number): string | null;
  /** Label of the selected entity (its name); idle for `null`. */
  useOption: (id: number | null) => RemoteData<ReactNode>;
  useOptions: (
    query: string,
    target: TargetOf<E>
  ) => RemoteData<Array<{ value: number; label: ReactNode }>>;
  /**
   * Hook: creates an entity from a typed name and resolves its id (`null`
   * when it failed); `null` when the user may not.
   */
  useCreate: (
    target: TargetOf<E>
  ) => ((name: string) => Promise<number | null>) | null;
  /** Shows the entity beside the form (display mode: its name opens it). */
  open: (id: number) => void;
  /** Hook: where the entity's own page is, in the browsing scope. */
  usePageHref: (id: number) => string;
  /** Hook: what the card on hovering the name shows (idle for `null`). */
  useCard: (id: number | null) => RemoteData<EntityCardData>;
  /** The user picked `id`; the host may fill other fields from it. */
  onPick: (pick: { spec: FieldSpecOf<'entity-ref'>; id: number }) => void;
};

/** A source per entity a form's `entity-ref` fields can point at: a missing one does not compile. */
export type EntityRefConfig = {
  [E in EntityRefEntity]: EntityRefSource<E>;
};

/**
 * `number | null` reference to another entity, searched through the source
 * of the spec's entity; shown, the name opens the entity (legacy
 * `ResourceConnectFormBlock`). In a list whose rows hold only this field it
 * is a line per entity.
 */
export function createEntityRefField(
  config: EntityRefConfig
): FieldComponent<'entity-ref'> {
  return function EntityRefField({ spec, path, hideLabel }) {
    const source = config[spec.entity];
    return (
      // A source's hooks differ per entity: a spec changing entity remounts.
      <EntityRefControl
        key={spec.entity}
        source={source}
        spec={spec}
        path={path}
        hideLabel={hideLabel}
      />
    );
  };
}

function EntityRefControl<E extends EntityRefEntity>({
  source,
  spec,
  path,
  hideLabel
}: {
  source: EntityRefSource<E>;
  spec: FieldSpecOf<'entity-ref'> & TargetOf<E>;
  path: string;
  hideLabel?: boolean;
}) {
  const { t } = useTranslation('patterns');
  const ctx = useFieldContext();
  const create = source.useCreate(spec);
  const id: number | null = useWatch({ name: path }) ?? null;

  if (ctx.mode === 'display') {
    if (id === null) {
      return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
    }
    return (
      <FieldControlBase spec={spec} hideLabel={hideLabel}>
        <EntityLink id={id} source={source} />
      </FieldControlBase>
    );
  }

  const selector: AsyncSelectorSource<number, number> = {
    useOption: source.useOption,
    useOptions: query => source.useOptions(query, spec),
    optionValueToFormValue: value => value,
    optionValueToKey: value => String(value)
  };

  return (
    <FormField
      name={path}
      render={({ field }) => (
        <FieldControlBase spec={spec} hideLabel={hideLabel}>
          <AsyncSelector<number, number>
            source={selector}
            value={field.value ?? null}
            onChange={picked => {
              field.onChange(picked);
              if (picked !== null) {
                source.onPick({ spec, id: picked });
              }
            }}
            onBlur={field.onBlur}
            name={field.name}
            triggerRef={field.ref}
            placeholder={t('SchemaForm.reference.select', {
              noun: source.noun
            })}
            searchPrompt={t('SchemaForm.reference.search', {
              noun: source.noun
            })}
            emptyContent={null}
            appearance={{ type: 'standalone' }}
            autoFocus={false}
            create={create}
          />
        </FieldControlBase>
      )}
    />
  );
}

/** The entity's name over a read-only input, as a link (like `UrlField`). */
function EntityLink({
  id,
  source
}: {
  id: number;
  source: Pick<
    EntityRefSource<EntityRefEntity>,
    'useOption' | 'useCard' | 'usePageHref' | 'open'
  >;
}) {
  const ctx = useFieldContext();
  const card = source.useCard(id);
  const pageHref = source.usePageHref(id);
  const onOpen = () => source.open(id);
  const name = rd
    .fullJourney(rd.useLastWithPlaceholder(source.useOption(id)))
    .initially(null)
    .wait(<Skeleton className="h-4 w-32" />)
    .catch(renderError)
    .map(label => label);
  return (
    // Laid out like `InputOverlay`, but the name is a real button: Tab
    // reaches it, focus shows the card as hovering does, Enter opens. The
    // whole row triggers the card, so it lines up with the field.
    <EntityHoverCard card={card} onOpen={onOpen} pageHref={pageHref}>
      <div className="relative w-full [&_input]:text-transparent">
        <div className="absolute top-1/2 left-0 flex max-w-full -translate-y-1/2">
          <button
            type="button"
            onClick={onOpen}
            className={cn(
              overlayVariants({ size: ctx.size, variant: ctx.variant }),
              'rounded-sm',
              focusRings.focus.visible
            )}
          >
            {name}
          </button>
        </div>
        <Input readOnly variant={ctx.variant} size={ctx.size} value="" />
      </div>
    </EntityHoverCard>
  );
}
